import { Plus, Globe, X } from 'lucide-react';
import { useState } from 'react';
import { CountriesTable } from '../components/CountriesTable';
import { CountryDetailModal } from '../components/CountryDetailModal';
import { CreateCountryModal } from '../components/CreateCountryModal';
import { DeleteCountryModal } from '../components/DeleteCountryModal';
import { EditCountryModal } from '../components/EditCountryModal';
import { ToggleCountryModal } from '../components/ToggleCountryModal';
import { useCountries } from '../hooks/useCountries';
import Protected from '@/shared/components/ahkinia/Protected/Protected';
import { SectionHeader } from '@/shared/components/ui/header';
import { Input } from '@/shared/components/ui/input';
import { UnifiedButton } from '@/shared/components/ui/unified-button';
import { PERMISSIONS } from '@/shared/constants/permissions';

export default function CountriesPage() {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [selectedCountryId, setSelectedCountryId] = useState<string>('');
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingCountryId, setEditingCountryId] = useState<string>('');
  const [toggleModalOpen, setToggleModalOpen] = useState(false);
  const [togglingCountryId, setTogglingCountryId] = useState<string>('');
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deletingCountryId, setDeletingCountryId] = useState<string>('');

  const { countries, pagination } = useCountries({
    page,
    pageSize,
    searchTerm: searchTerm || undefined,
  });

  const togglingCountry = countries.find((c) => c.id === togglingCountryId);
  const deletingCountry = countries.find((c) => c.id === deletingCountryId);

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setPage(1);
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      setPage(1);
    }
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    setPage(1);
  };

  const handleViewDetail = (countryId: string) => {
    setSelectedCountryId(countryId);
    setDetailModalOpen(true);
  };

  const handleEdit = (countryId: string) => {
    setEditingCountryId(countryId);
    setEditModalOpen(true);
  };

  const handleToggle = (countryId: string) => {
    setTogglingCountryId(countryId);
    setToggleModalOpen(true);
  };

  const handleDelete = (countryId: string) => {
    setDeletingCountryId(countryId);
    setDeleteModalOpen(true);
  };

  const handleOpenCreateModal = () => {
    setCreateModalOpen(true);
  };

  const handleCloseToggleModal = () => {
    setToggleModalOpen(false);
    setTogglingCountryId('');
  };

  const handleCloseDeleteModal = () => {
    setDeleteModalOpen(false);
    setDeletingCountryId('');
  };

  const hasData = countries.length > 0;

  return (
    <div data-testid="countries-page" className="flex flex-col gap-6 p-6">
      <SectionHeader
        sectionIcon={<Globe />}
        title="Países"
        subtitle="Gestión de países disponibles en el sistema"
      >
        <Protected permissions={PERMISSIONS.COUNTRIES_CREATE}>
          <UnifiedButton
            data-testid="create-country-btn"
            icon={<Plus />}
            onClick={handleOpenCreateModal}
          >
            Registrar país
          </UnifiedButton>
        </Protected>
      </SectionHeader>

      <div className="flex items-center gap-4">
        <Input
          data-testid="search-countries-input"
          placeholder="Buscar por nombre o código ISO..."
          value={searchTerm}
          onChange={handleSearchChange}
          onKeyDown={handleSearchKeyDown}
          className="max-w-[300px]"
        />
        {searchTerm && (
          <UnifiedButton
            data-testid="clear-search-btn"
            variant="outline"
            onClick={handleClearSearch}
          >
            <X />
          </UnifiedButton>
        )}
      </div>

      {hasData ? (
        <CountriesTable
          data={countries}
          pagination={pagination}
          onPageChange={handlePageChange}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setPage(1);
          }}
          onViewDetail={handleViewDetail}
          onEdit={handleEdit}
          onToggle={handleToggle}
          onDelete={handleDelete}
        />
      ) : (
        <div
          data-testid="countries-empty-state"
          className="flex flex-col items-center justify-center py-12 rounded-xl bg-background border-outline"
        >
          <Globe size={48} className="text-primary mb-4 opacity-50" />
          <p className="text-primary mb-4">No hay países configurados. Registra uno nuevo.</p>
          <Protected permissions={PERMISSIONS.COUNTRIES_CREATE}>
            <UnifiedButton
              data-testid="empty-create-country-btn"
              icon={<Plus />}
              onClick={handleOpenCreateModal}
            >
              Registrar país
            </UnifiedButton>
          </Protected>
        </div>
      )}

      <CreateCountryModal open={createModalOpen} onOpenChange={() => setCreateModalOpen(false)} />

      <EditCountryModal
        countryId={editingCountryId}
        open={editModalOpen}
        onOpenChange={() => {
          setEditModalOpen(false);
          setEditingCountryId('');
        }}
      />

      <ToggleCountryModal
        countryId={togglingCountryId}
        countryName={togglingCountry?.officialName || ''}
        currentStatus={togglingCountry?.isActive || false}
        open={toggleModalOpen}
        onClose={handleCloseToggleModal}
      />

      <DeleteCountryModal
        countryId={deletingCountryId}
        countryName={deletingCountry?.officialName || ''}
        open={deleteModalOpen}
        onClose={handleCloseDeleteModal}
      />

      <CountryDetailModal
        countryId={selectedCountryId}
        open={detailModalOpen}
        onOpenChange={setDetailModalOpen}
      />
    </div>
  );
}
